/**
 * Demo Page
 * Interactive demo with chat widget for selected brand
 */

import React, { useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ChatWidget from '../components/ChatWidget/ChatWidget';

// Demo brand configurations
const demoBrands = {
    'topsail-hardware': {
        id: 'Topsail Hardware',
        name: 'Topsail Hardware',
        industry: 'retail',
        sampleQuestions: [
            "What are your hours?",
            "Do you have power drills in stock?",
            "Can you cut lumber to size?",
            "Do you offer delivery?",
        ],
    },
    'harbor-ridge-municipal': {
        id: 'Harbor Ridge Municipal',
        name: 'Harbor Ridge Municipal',
        industry: 'government',
        sampleQuestions: [
            "When is trash pickup?",
            "How do I get a building permit?",
            "What are the park hours?",
            "How do I pay my water bill?",
        ],
    },
    'captains-table': {
        id: "The Captain's Table",
        name: "The Captain's Table",
        industry: 'restaurant',
        sampleQuestions: [
            "Do you take reservations?",
            "What's on the menu?",
            "Do you have outdoor seating?",
            "Do you serve brunch?",
        ],
    },
    'coastal-auto': {
        id: 'Coastal Auto Group',
        name: 'Coastal Auto Group',
        industry: 'automotive',
        sampleQuestions: [
            "Do you have any trucks in stock?",
            "How do I book a service appointment?",
            "What financing options do you offer?",
            "Can I get a trade-in estimate?",
        ],
    },
    'atlantic-shore-realty': {
        id: 'Atlantic Shore Realty',
        name: 'Atlantic Shore Realty',
        industry: 'real_estate',
        sampleQuestions: [
            "I'm looking to buy a home in Yarmouth — where do I start?",
            "How do I book a consultation with an agent?",
            "What areas do you cover?",
            "How long does it take to sell a home?",
        ],
    },
    'south-shore-trades': {
        id: 'South Shore Trades',
        name: 'South Shore Trades',
        industry: 'trades',
        sampleQuestions: [
            "Can someone come look at my furnace today?",
            "Do you carry heat pumps?",
            "How do I get a quote for a new water heater?",
            "What's your service area?",
        ],
    },
    // Real, live concierge — kept out of the fictional demo switcher (see the
    // "in the wild" feature on the landing page), but reachable here so the
    // landing-page showcase links to a working try-it experience.
    'chestnut-holler': {
        id: 'Chestnut Holler Guesthouse',
        name: 'Chestnut Holler Guesthouse',
        industry: 'hospitality',
        // Concierge persona — independent local guide for the Barrington area.
        disclaimer: 'Independent Chestnut Holler local guide — not official tourism. Hours and events change; verify before you go.',
        sampleQuestions: [
            "What's there to do around Barrington?",
            "Can you recommend a good spot for seafood?",
            "What are the best beaches nearby?",
            "Quels sont les restaurants du coin?",
        ],
    },
};

function DemoPage() {
    const { brandSlug } = useParams();
    const brand = demoBrands[brandSlug];
    const chatRef = useRef(null);

    // Auto-open the chat so visitors don't have to find the launcher icon.
    // Brief delay lets the page paint first, so the slide-up animation draws the eye.
    useEffect(() => {
        if (!brand) return;
        const timer = setTimeout(() => chatRef.current?.open(), 800);
        return () => clearTimeout(timer);
    }, [brandSlug, brand]);

    if (!brand) {
        return (
            <div className="container mt-5">
                <div className="alert alert-danger">
                    <h4>Brand not found</h4>
                    <Link to="/" className="btn btn-primary mt-3">
                        ← Back to Home
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="demo-page">
            {/* Navbar */}
            <nav className="navbar navbar-dark bg-lighthouse-gradient">
                <div className="container">
                    <Link to="/" className="navbar-brand">
                        ⚓ Lighthouse
                    </Link>
                    <div className="d-flex gap-3 align-items-center">
                        <Link to="/home" className="nav-link text-white">Home</Link>
                        <Link to="/subscription" className="nav-link text-white">Pricing</Link>
                        <span className="text-white-50 small">Demo: {brand.name}</span>
                    </div>
                </div>
            </nav>

            {/* Demo Content */}
            <div className="container mt-5">
                <div className="row">
                    <div className="col-lg-8 mx-auto">
                        <div className="card shadow-sm">
                            <div className="card-body p-5">
                                <h2 className="card-title mb-4">{brand.name} Demo</h2>

                                <div className="alert alert-info mb-4">
                                    <h5
                                        className="alert-heading"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => chatRef.current?.open()}
                                    >
                                        ✨ Try the Live Chat!
                                    </h5>
                                    <p className="mb-0">
                                        The chat opens automatically in the bottom-right corner —{' '}
                                        <a
                                            href="#"
                                            onClick={(e) => { e.preventDefault(); chatRef.current?.open(); }}
                                            className="alert-link"
                                        >
                                            reopen it here
                                        </a>
                                        {' '}if you close it, or use the lighthouse icon.
                                        Ask questions about hours, products, services, or anything else!
                                    </p>
                                </div>

                                <h4 className="mb-3">Sample Questions:</h4>
                                <ul className="list-unstyled">
                                    {brand.sampleQuestions.map((question, index) => (
                                        <li key={index} className="mb-2">
                                            💬 "{question}"
                                        </li>
                                    ))}
                                </ul>

                                <div className="text-center mt-4">
                                    <Link to="/" className="btn btn-outline-secondary">
                                        ← Try Another Demo
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Chat Widget */}
            <ChatWidget
                ref={chatRef}
                brandId={brand.id}
                brandName={brand.name}
                disclaimer={brand.disclaimer}
            />
        </div>
    );
}

export default DemoPage;
